/*global define*/
define(
	'BaseTransferModel',[
		'require',
		'jquery',
		'underscore',
		'backbone',
		'BaseModel',
		'BaseErrorModelCollection',
		'BaseServiceModel',
		'jquery.urlParams' // exposes $.getUrlParams
	],
	function (require, $, _, Backbone, BaseModel, BaseErrorModelCollection) {
		'use strict';

		////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		// Main class definition
		////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		/**
		 * Base transfer model
		 * @class BaseTransferModel
		 */
		var BaseTransferModel = BaseModel.extend(
			/**
			 * @lends BaseModel.prototype
			 */
			{
				className: 'BaseTransferModel',
				/**
				 * List of service Ids mapped to backbone models
				 * @type {Hash}
				 */
				serviceIdToBackboneModelMapping: {
					baseServiceModelId: 'BaseServiceModel', // used by the generic base service model
					getAccountList: 'PayBillGetAccountListServiceModel',
					getCreditCardList: 'CreditCardCollection',
					getAmountDueByAccountNumber: 'PayBillGetAmountDueAccountNumberServiceModel',
					paySingleAccountBills: 'PayBillPaySingleAccountBillsServiceModel',
					payBillDetails: 'PayBillServiceModel',
					viewBillDetails: 'ViewBillServiceModel',
					showBillList: 'BillsListModel',
					paymentHistory: 'PaymentHistoryServiceModel',
					getCurrentPlan: 'ChangePlanServiceModel',
					changeRatePlan: 'ChangePlanServiceModel',
					getExtraDataResetUsage: 'ExtraDataUsageServiceModel',
                    getExtraDataHistoryDetail: 'ExtraDataUsageServiceModel',
					prepaidMobileRoamingSettings: 'PrepaidMobileRoamingSettingsServiceModel',
					postpaidMobileRoamingSettings: 'PostpaidMobileRoamingSettingsServiceModel',
					postpaidMobileRoamingCharges: 'PostpaidMobileRoamingChargePromptServiceModel',
					postpaidMobileRoamingUsageData: 'PostpaidMobileRoamingChargePromptServiceModel',
					postpaidOWBRoamingUsageData: 'PostpaidOWBRoamingUsageDataServiceModel',
					callSettings: 'CallSettingsServiceModel',
					callDiversionSettings: 'CallDiversionSettingsServiceModel',
					simReplacement: 'SimReplacementServiceModel',
					sqTool: 'SqToolServiceModel',
					unlockPUK: 'UnlockPukServiceModel',
                    searchQuery: 'SearchQueryModel',
                    searchRecommendedResult: 'SearchRecommendedResultModel',
                    searchListingResults: 'SearchListingResultsModel',
                    searchListRefinementResults: 'SearchListRefinementResultsModel',
                    postpaidMobileRoamingUsage:'PostpaidRoamingUsageServiceModel',
                    searchArticles: 'HelpAndSupportSearchModel',
                    optusOffers: "Models/OptusOfferModel",
					usageSummaryTable: 'UsageSummaryTableServiceModel',
					usageSummaryGraph: 'UsageSummaryGraphServiceModel',
				    usageSummaryHistory: 'UsageSummaryHistoryServiceModel',
				    usageDetails: 'UsageDetailsServiceModel',
				    usageDetailsVoip: 'UsageDetailsVoipServiceModel',
				    usageDetailsExcess: 'UsageDetailsExcessServiceModel',
				    usageDetailsPeakOffPeak: 'UsageDetailsPeakOffPeakModel',
				    usageDetailsUnlimited: 'UsageDetailsUnlimitedServiceModel',
                    movieRewardsCheckEligibility:"MovieRewardsEligibilityCheckModel",
                    usageSummaryTieredGraph: 'UsageSummaryTieredServiceGraphModel',
                    serviceSelector: 'ServiceCollection',
                    billingSummary: 'BillingSummaryMetaModel',
                    postpaidRoamingBoltOnStartPurchase:"RoamingBoltOnStartPurchaseServiceModel",
                    postpaidRoamingBoltOnAndCountry:"RoamingBoltOnAndCountryServiceModel",
                    postpaidRoamingBoltOnDates:"RoamingBoltOnDatesServiceModel",
                    postpaidRoamingBoltOnSubmitOrder:"RoamingBoltOnSubmitOrderServiceModel",
                    paymentAdviceId: 'PaymentAdviceModel',
                    paymentExtensionId: 'PaymentExtensionModel',

					// FIXME This needs to be a new model type.
					resetUsageDataModel: 'UsageSummaryGraphServiceModel',
					prepaidUsageMeters: 'PrepaidUsageSummaryServiceModel',

					businessSearch: 'Models/search/business/BusinessSearchMetaModel',
					autoCompleteSearch: 'GlobalHeaderSearchRecommendedModel',
                    CaseStudiesListing: 'Models/business/CaseStudyListServiceModel',

					newsFeedListing: 'NewsFeedListServiceModel'
				},

				/**
				 * Current transfer action: save|fetch or empty
				 * @type {String}
				 */
				curTransferAction: '',

				/**
				 * Set this to true if you want to allow fetching data when the instance status is 'error'.
				 * Default value is false.
				 * @type {Boolean}
				 */
				retryError: false,

				/**
				 * max number of times we retried to do a fetch or save
				 * @type {Integer}
				 */
				//retryCount: 0,

				/**
				 * max number of times to retry a fetch or save
				 * @type {Integer}
				 */
				//maxRetryCount: 10,

				defaults: {
					/**
					 * Service id. What is the service identifier, or name? e.g. 'callSettings'
					 * @type {String}
					 */
					id: "",
					/**
					 * possible values are ok|error|retry|postload
					 * @type {String}
					 */
					status: "ok",
					/**
					 * How much time to wait before retrying to load the model
					 * @type {Integer}
					 */
					retryWaitTime: 1000,
					/**
					 * Errors received from the backend
					 * @type {BaseTransferModelErrorCollection}
					 */
					errors: null
				},

				/**
				 * Enforce default values for 'errors' and 'data' attributes at init time
				 * @param {Object} options
				 */
				initialize: function (options) {

					var self = this,
						dataConstructor = self.getInternalDataModelConstructor(options.id);

					// call parent method
					BaseModel.prototype.initialize.apply(self, [].slice.call(arguments));

					self.alwaysCoerceAttrToModel('errors', BaseErrorModelCollection);

					self.alwaysCoerceAttrToModel('data', dataConstructor);

				},

				/**
				 * Shortcut method to retrieve a variable from the "data" model attribute.
				 * Same as doing this.get('data').get('your_attribute')
				 *
				 * As an extra, if you call this method without arguments, it will return the 'data' model attribute.
				 * this.get('data') === this.data()
				 *
				 * @example
				 * For:
				 * this.get('data').get('your_attribute') => 'your_value'
				 * Then:
				 * this.data('your_attribute') => 'your_value'
				 *
				 * For:
				 * this.get('data').set('your_attribute', 'your_value');
				 * this.get('data').get('your_attribute') => 'your_value'
				 * Then:
				 * this.data('your_attribute', 'your_value') => 'your_value'
				 * this.data('your_attribute') => 'your_value'
				 *
				 * @param {string|Object} name Name of the data attribute to retrieve, or hash of attributes to set
				 * @param {*} value For a key/value pair scenario: value to set. For a set(attributes, options) scenario, it's the options parameter
				 * @param {Object} options For a key/value pair scenario: options to use when setting the value.
				 * @returns {Backbone.Model|*}
				 */
				data: function (name, value, options) {
					var self = this,
						methodName = 'data',
						internalData = self.get('data'),
						args = arguments,
						argCount = args.length,
						ret;

					if (argCount === 0) {
						ret = internalData;
					} else {

						//if (!internalData) {
						//	throw new Error(self.report(methodName, 'no internal data to retrieve'))
						//}

						var nameType = typeof name,
							attributes = (nameType === 'object') && name;

						// if 'get'
						if ((nameType === 'string') && (argCount === 1) && !attributes) {
							ret = internalData.get(name);
						} else { // else, 'set'
							// this allows us to do either set(name, value, options) or set(attributes, options) as per Backbone.Model's set method
							ret = internalData.set.apply(internalData, [].slice.call(args));
						}
					}

					return ret;
				},

				/**
				 * Returns the data model's class name for the "data" attribute.
				 * @param [serviceId]
				 * @returns {String}
				 */
				getInternalDataModelClassName: function (serviceId) {
					var self = this,
						methodName = 'getInternalDataModelClassName';

					serviceId = serviceId || self.get('id');

					var serviceIdToBackboneModelMapping = self.serviceIdToBackboneModelMapping,
						backboneServiceModelName = serviceId && serviceIdToBackboneModelMapping[serviceId];

					if (!backboneServiceModelName) {
						console.log('unable to identify service model based on this id "' + serviceId + '"');
						//throw new Error(self.report(methodName, 'unable to identify service model based on this id "' + serviceId + '"'));
					}

					return backboneServiceModelName;
				},

				/**
				 * Returns the data model constructor function for the "data" attribute.
				 * @param [serviceId]
				 * @returns {Function|Backbone.Model|Backbone.Collection}
				 */
				getInternalDataModelConstructor: function (serviceId) {
					var self = this,
						methodName = 'getInternalDataModelConstructor',
						backboneServiceModelName = self.getInternalDataModelClassName(serviceId),
						contructor;

					// This library has to already be loaded by requirejs, or it will fail to run synchronously.
					// use require.js to make sure we have access to that backbone service model constructor
					contructor = require(backboneServiceModelName);

					if (!contructor) {
						throw new Error(self.report(methodName, 'unable to retrieve internal data model constructor function for class="' + self.get('id') + '". Check that this has been pre-loaded in requirejs already.'));
					}
					return contructor;
				},

				/**
				 * Returns a URL containing parameters as requested from the queryParams property (defined in a service model JS class).
				 * The queryParams object is a hash where the key will become the URL parameter name and the value is a keypath
				 * to an attribute of the service model itself.
				 *
				 * @example
				 * var MyServiceModel = BaseServiceModel.extend({
				 *     queryParams = {
				 *         param1: 'attribute1'
				 *     }
				 * });
				 *
				 * var transferModel = new BaseTransferModel({
				 *     readUrl: '/somewhere',
				 *     data: {
				 *         attribute1: 1
				 *     }
				 * });
				 *
				 * transferModel.getServiceModelUrlParams(transferModel.get('readUrl')) // -> request to '/somewhere?param1=1'
				 * transferModel.fetch() // -> request to '/somewhere?param1=1'
				 *
				 * @param {string} initialUrl Initial URL
				 * @returns {string} Modified url, after adding the parameter as requested from the service model's class
				 */
				getServiceModelUrlParams: function (initialUrl) {
					var self = this,
						setUrlParams = $.setUrlParams,
						serviceModel = self.data(),
						queryParams = serviceModel && serviceModel.queryParams,
						paramHash = {},
						ret = initialUrl;

					if (queryParams) {
						$.each(queryParams, function(paramName, keypath) {
							//DEBUG
							//console.log('going to check %s.%s.', paramName, keypath);

							var targetModelInfo = serviceModel.nestedGetModel(keypath),
								value = targetModelInfo.model && targetModelInfo.model.get(targetModelInfo.keypath);

							// check that the param value is not undefined,
							// if it is, use an empty string instead; otherwise, a jQuery.extend issue will not create any key when its value is undefined
							paramHash[paramName] = (typeof(value) === 'undefined')? '' : value;
						});

						ret = setUrlParams(paramHash, initialUrl);
					}

					return ret;
				},

				/**
				 * @see {@linkplain http://backbonejs.org/#Model-sync}
				 * @param {String} httpMethod the CRUD method ("create", "read", "update", or "delete")
				 * @param {Backbone.Model} model the model to be saved (or collection to be read)
				 * @param {Object} [options] success and error callbacks, and all other jQuery request options
				 */
				sync: function (httpMethod, model, options) {
					options = options || {};

					var self = this,
						serviceModel = self.data(),
						orgBeforeSend = options.beforeSend;

					// if we have custom query params to handle
					if (serviceModel && serviceModel.queryParams) {
						/**
						 * Customise the URL parameters before executing the ajax request.
						 *
						 * A pre-request callback function that can be used to modify the jqXHR (in jQuery 1.4.x, XMLHTTPRequest)
						 * object before it is sent. Use this to set custom headers, etc.
						 * The jqXHR and settings objects are passed as arguments.
						 * This is an Ajax Event.
						 * Returning false in the beforeSend function will cancel the request.
						 * As of jQuery 1.5, the beforeSend option will be called regardless of the type of request.
						 * @see {@linkplain http://api.jquery.com/jQuery.ajax/ }
						 * @inner
						 * @param {jqXHR} jqXHR
						 * @param {Object} ajaxOptions
						 */
						var ourBeforeSend = function (jqXHR, ajaxOptions) {
							var url = ajaxOptions.url;
							//DEBUG
							//console.log('ajaxOptions.url = ', url);
							ajaxOptions.url = self.getServiceModelUrlParams(ajaxOptions.url);
						};

						// if beforeSend was already implemented in the options, overload it
						if (orgBeforeSend) {
							ourBeforeSend = (function (oldFc, newFc) {
								return function () {
									newFc.apply(this, arguments);
									return oldFc.apply(this, arguments);
								};
							})(orgBeforeSend, ourBeforeSend);
						}

						// use our beforeSend() callback
						options.beforeSend = ourBeforeSend;
					}

					return BaseModel.prototype.sync.apply(self, [httpMethod, model, options]);
				},

				/**
				 * Creates and adds an error to the error list
				 * @param {String} errorId
				 * @param {String} errorMsg
				 * @param {Object} [extraErrorProperties]
				 * @returns {*}
				 */
				addErrorMsg: function (errorId, errorMsg, extraErrorProperties) {
					var self = this,
						errorDef = {
							errorId: errorId,
							errorMsg: errorMsg
						};

					if (extraErrorProperties) {
						_.extend(errorDef, extraErrorProperties);
					}

					// set error status manually
					self.get('errors').add([ errorDef ], { // merge to any potential error message that has the same id
						merge: true
					});
					return self;
				},

				/**
				 * Check the server response integrity
				 * @param {Object} response raw JSON object from the server response
				 * @return {*} Returns current model for chainability
				 */
				checkServerResponse: function (response) {
					var self = this;

					// validate that the server response contains id and status attributes
					if (!response || !response.id || !response.status) {
						self.set('status', 'error')
							.addErrorMsg('invalid_server_response', 'Server response could not be interpreted.', {
								response: response
							});
					}
					return self;
				},

				/**
				 * Method to process the server response after an ajax request (get/post) for fetch() or save().
				 * @param {Object} options
				 * @returns {BaseTransferModel}
				 */
				processServerResponse: function (options) {
					var self = this,
						methodName = 'processServerResponse',
						status = self.get('status'),
						retryWaitTime = self.get('retryWaitTime'),
						callerContext = options.callerContext;

					//DEBUG
					//console.log(self.report(methodName, ' status = %o'), status);

					if (status === 'ok') {
						//DEBUG
						//console.log(self.report(methodName, 'assigned it to current object = %o'), self);

						var successCallback = options && options.success;
						if (_.isFunction(successCallback)) {
							successCallback.apply(callerContext.context, callerContext.args);
						}

						self.curTransferAction = '';
					} else if (status === 'retry') {
						var curTransferAction = self.curTransferAction;
						if (curTransferAction === 'fetch' || curTransferAction === 'save') {

							//DEBUG
							//console.log(self.report(methodName, ' retryWaitTime = %o'), retryWaitTime);

							setTimeout(function () {
								if (curTransferAction === 'fetch') {
									self.fetch(options);
								} else {
									self.save({}, options);
								}
							}, retryWaitTime);
						} else {
							throw new Error(self.report(methodName, 'unknown action to retry'));
						}
					} else if (status === 'error') {
						//DEBUG
						//console.warn(self.report(methodName, 'server response status=' + status + ': '), self);

						var errorCallback = options && options.error;
						if (_.isFunction(errorCallback)) {
							errorCallback.apply(callerContext.context, callerContext.args);
						}

						self.curTransferAction = '';
					} else if (status !== 'postload') {
						// check with David what to do when the status is postload but do not throw an error for now
						throw new Error(self.report(methodName, 'unexpected status[' + status + ']'));
					}
					return self;
				},

				/**
				 * Returns true if this BaseTransferModel is busy fetching/saving data.
				 * @return {Boolean}
				 */
				isBusy: function () {
					return !!this.curTransferAction;
				},

				/**
				 * Has this model been saved to the server yet?
				 * If the internal service model does not yet have an id, it is considered to be new.
				 * @see {@plainlink http://backbonejs.org/#Model-isNew}
				 * @returns {boolean}
				 */
				isNew: function () {
					var data = this.data();
					if (data) { // if we have a service model
						return data.isNew(); // it determines if the BaseTransferModel is new
					}
					return true; // if no service model, just return true
				},

				/**
				 * Fetch method, as per the Backbone.Model's fetch() method.
				 * Note that it will use the attributes of this model to interpret the server response status.
				 * If status = 'ok', no need to fetch anything.
				 * If status = 'postload', fetch server data
				 * If status = 'retry', try to fetch server data again ONLY if the previous sync method was a fetch().
				 * @param {Object} options
				 * @param {Boolean} options.force Set to true to enable re-fetching from a model where status=ok
				 * @param {Boolean} options.retryError Set to true to enable re-fetching from a model where status=error
				 * @return {jqXHR}
				 */
				fetch: function (options) {
					options = options || {};

					var self = this,
						methodName = 'fetch',
						forceFetch = !!(options && options.force),
						augmentedOptions = {},

						retryError = ('retryError' in options) ? options.retryError : self.retryError,
						status = self.get('status');

					//DEBUG
					// console.warn('self = %o', self);
					//console.log(self.report(methodName, ' status = %o'), status);
					//console.log(self.report(methodName, ' self.curTransferAction = %o'), self.curTransferAction);

					switch (status.toLowerCase()) {
					case 'ok':
						if (!forceFetch) { // if we do not have to force fetch,
							// then do nothing, data is already there
							self.curTransferAction = '';
							return null;
						}
						break;
					case 'error':
						if (!retryError) {
							// do nothing
							self.curTransferAction = '';
							return null;
						}
						break;
					case 'retry':
						if (self.curTransferAction !== methodName) {
							throw new Error(self.report(methodName, 'current instance is already trying to fetch data.'));
						}
						break;
					case 'postload':
						break;
					case 'ondemand':
						break;
					default:
						throw new Error(self.report(methodName, 'unsupported status value.'));
					}

					self.curTransferAction = methodName;

					//DEBUG
					//console.log(self.report(methodName, 'initial options = '), options);

					_.extend(augmentedOptions,
						options,
						{
							success: function (model, resp, opt) {

								options.callerContext = {
									scenario: 'ajaxSuccess',
									context: this,
									args: [].slice.call(arguments)
								};

								self.checkServerResponse(resp).processServerResponse(options);
							},
							error: function (model, xhr, opt) {
								// set error status manually
								self.addErrorMsg('ajax_error', 'A network error has occurred. Please try again later.', {
									xhr: xhr
								});

								// set error status manually
								self.set('status', 'error');

								options.callerContext = {
									scenario: 'ajaxError',
									context: this,
									args: [].slice.call(arguments)
								};

								self.processServerResponse(options);
							}
						}
					);

					// remove retryError from the augmentedOptions, if any
					delete augmentedOptions.retryError;

					return BaseModel.prototype.fetch.call(self, augmentedOptions);
				},

				/**
				 * Save data handler.
				 * This method is designed to ONLY send the JSON representation of the internal data model. i.e. self.data() -> POST(JSON)
				 * @see Backbone's Model.save() documentation
				 * @param {Object} data
				 * @param {Object} options
				 * @return {BaseTransferModel}
				 */
				save: function (data, options) {
					var self = this,
						augmentedOptions = {},
						methodName = 'save',
						status = self.get('status');

					//DEBUG
					// console.warn('self = %o', self);
					//console.log(self.report(methodName, ' status = %o'), status);
					//console.log(self.report(methodName, ' self.curTransferAction = %o'), self.curTransferAction);

					if (status === 'retry') {
						if (self.curTransferAction !== methodName) {
							throw new Error(self.report(methodName, 'current instance is already trying to save data.'));
						}
					} else if (status === 'postload') {
						throw new Error(self.report(methodName, 'current instance is not fully loaded yet.'));
					}

					options = options || {};

					self.curTransferAction = methodName;

					_.extend(
						augmentedOptions,
						options,
						{
							// set our own data to send from the inner 'data' attribute
							data: ('data' in options) ? options.data : JSON.stringify(self.data().toJSON()),

							// custom success handler
							success: function (model, resp, opt) {

								options.callerContext = {
									context: this,
									args: [].slice.call(arguments)
								};

								self.processServerResponse(options);

							},

							// custom error handler
							error: function (model, xhr, opt) {
								// set error status manually
								self.addErrorMsg('ajax_error', 'A network error has occurred. Please try again later.', {xhr: xhr});

								// set error status manually
								self.set('status', 'error');

								options.callerContext = {
									context: this,
									args: [].slice.call(arguments)
								};

								self.processServerResponse(options);
							}
						}
					);

					return BaseModel.prototype.save.call(self, data, augmentedOptions);
				},

				/**
				 * Runs different callback functions when the current model is being fetched.
				 * @param {Object} [options] Options
				 * @param {Function} [options.success] Run this function when the model data has been successfully fetched from the server
				 * @param {Function} [options.error] Run this function when the model fetch request has received an error from the server
				 * @param {Function} [options.wip] Run this function when the model fetch request has already been sent and we're waiting for the server response. This function is called only once.
				 * @param {Function} [options.retry] Run this function when the model fetch request has received a "retry" response from the server. Note that the BaseTransferModel will retry to fetch automatically, so this function can be triggered multiple times.
				 */
				whenFetched: function (options) {
					var self = this,
						anonFc = function () {
						},
						success = options.success || anonFc,
						wip = options.wip || anonFc,
						retry = options.retry || anonFc,
						error = options.error || anonFc;

					//DEBUG
					//console.warn('serviceWrapperModel = %o', serviceWrapperModel.toJSON());
					//console.warn('serviceWrapperModel status = %o', serviceWrapperModel.get('status'));

					// Check the current service wrapper status
					var status = self.get('status').toLowerCase(),
						syncEventName = 'sync',
						errorEventName = 'error';

					// If it's already loaded, just go to success state
					if (status === 'ok') {
						success();
					} else if (status === 'postload' || status ==='ondemand') { // If it's supposed to be postloaded or load on demand,
						// check when the status attribute changes
						var statusHandler = function (curModel, resp, options) {
							var status = curModel.get('status'),
								isOk = status === 'ok';

							//DEBUG
							//console.warn('statusOkHandler: args = ', arguments);
							//console.warn('statusOkHandler: status = ', status);

							if (isOk || (status === 'error')) { // if status is 'ok' or 'error'
								// stop listening to 'sync' and 'error' events
								curModel.off(syncEventName, statusHandler).off(errorEventName, statusHandler);

								if (isOk) {
									success();
								} else { // must be 'error'
									error();
								}
							} else if (status === 'retry') {
								retry();
							}
						};

						// listen to 'sync' and 'error' events
						self.on(syncEventName + ' ' + errorEventName, statusHandler);

						// go to the postload state in the meantime
						if (self.curTransferAction) {
							wip();
						}
					} else if (status === 'error') { // preload state is 'error'. We can't start the module.
						error();
					}
				} // end of whenFetched: function(options)
			}
		);
		return BaseTransferModel;
	}
);

